import React, { useContext } from "react";
import { commitMutation, graphql, useFragment } from "react-relay";

import moment from "moment";
import uuidv4 from "uuid/v4";

import AppContext from "~/common/AppContext";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import UserSelector, { type SingleValueType, type UserSelectorOption } from "~/common/UserSelect/UserSelector";
import addManagerAvatars from "~/legacy-assets/images/illustrations/AddManagerAvatars.png";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { inCalendarMode, inVideocallMode, isZoom } from "~/lib/utils";
import { environment } from "~/models";
import { Button, Tooltip, useModals } from "~/ui";
import { ButtonLink, Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import type { AddManagerModal_viewer$key } from "~/__generated__/AddManagerModal_viewer.graphql";
import type { AddManagerModalSetManagerMutation as MutationType } from "~/__generated__/AddManagerModalSetManagerMutation.graphql";

import styles from "./css/AddManagerModal.module.css";

const SetManagerMutation = graphql`
	mutation AddManagerModalSetManagerMutation($input: SetManagerInput!) {
		setManager(input: $input) {
			ok
			user {
				id
				email
				fullName
				image
				title
				department
				isGuest
				isActive
				isConfigured
				internalAttributes {
					manager {
						id
						fullName
					}
				}
			}
		}
	}
`;

export const DISMISS_ADD_MANAGER_KEY = "AddManagerModal__dismiss";
export const SILENCE_ADD_MANAGER_KEY = "AddManagerModal__silence";

interface AddManagerModalProps {
	userId: string;
	onClose: () => void;
}

const analyticsProperties = {
	fingerprint: uuidv4(),
	ux: "modal",
};

function AddManagerModal({ userId, onClose }: AddManagerModalProps) {
	const [selection, setSelection] = React.useState<UserSelectorOption | null>(null);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState(false);

	const { dismiss } = React.useContext(DismissibleElementsContext);

	React.useEffect(() => {
		dispatchAnalyticsTrack("[Add Manager Modal] Nudge Shown", analyticsProperties);
	}, []);

	function resolveManagerUserAnalyticsType(selection: UserSelectorOption | null) {
		if (!selection) return null;
		return selection.value ? "existing_user" : "to_be_invited_user";
	}

	const closeModal = React.useCallback(() => {
		onClose();
		dispatchAnalyticsTrack("[Add Manager Modal] Nudge Closed", analyticsProperties);
	}, [onClose]);

	const onSet = React.useCallback(async () => {
		if (!selection) {
			setError(true);
			return;
		}

		setLoading(true);

		/* eslint-disable camelcase */
		dispatchAnalyticsTrack("[Add Manager Modal] Nudge Completed", {
			manager_user_status: resolveManagerUserAnalyticsType(selection),
			manager_id: selection.value,
			...analyticsProperties,
		});

		/* eslint-enable camelcase */
		commitMutation<MutationType>(environment, {
			mutation: SetManagerMutation,
			variables: {
				input: {
					managerId: selection.value,
				},
			},
			onCompleted: ({ setManager }, err) => {
				setLoading(false);
				if (setManager?.ok) {
					if (err) {
						toast.error("Something went wrong, your manager wasn't set!");
					} else {
						dispatchAnalyticsTrack("Manager Set");
						toast.success("Manager set!");
						closeModal();
					}
				}
			},
		});
	}, [selection, closeModal]);

	const { openSettingsModal } = useContext(AppContext);
	const onRedirectionToSettings = React.useCallback(() => {
		dispatchAnalyticsTrack("[Add Manager Modal] Nudge Redirected", analyticsProperties);
		openSettingsModal("profile");
	}, [openSettingsModal]);

	const onSelectManager = React.useCallback((option: SingleValueType<UserSelectorOption>) => {
		if (!option || Array.isArray(option)) return;

		setSelection(option);
		setError(false);
	}, []);

	const dismissModal = React.useCallback(() => {
		dispatchAnalyticsTrack("[Add Manager Modal] Nudge Dismissed", analyticsProperties);
		dismiss(DISMISS_ADD_MANAGER_KEY);
		closeModal();
	}, [closeModal, dismiss]);

	const notShowAgain = React.useCallback(() => {
		dispatchAnalyticsTrack("[Add Manager Modal] Nudge Silenced", analyticsProperties);
		dismiss(SILENCE_ADD_MANAGER_KEY);
		closeModal();
	}, [closeModal, dismiss]);

	return (
		<Modal onClose={dismissModal}>
			<Modal.Header onClose={dismissModal}>
				<Modal.Title>Set Your Manager</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				<div className={styles.headerSubtitle}>
					Kickstart your 1-on-1s with suggested topics by setting your manager in Fellow.
				</div>

				<div>
					<img alt="avatars" src={addManagerAvatars} className={styles.illustration} />
				</div>

				<div className={styles.inputHeader}>Add your manager</div>

				<UserSelector
					excludeUserIDs={[userId]}
					doesNotReportUnder={userId}
					placeholder="Find your manager by name"
					onChange={onSelectManager}
				/>

				{error && <p className={styles.error}>You have not added a manager</p>}

				<div className={styles.note}>
					Update your manager anytime from your{" "}
					<Tooltip content="Manage Profile">
						<ButtonLink className={styles.templateTooltip} onClick={onRedirectionToSettings}>
							settings
						</ButtonLink>
					</Tooltip>
					.
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Modal.FooterSecondaryActions>
					<ButtonLink onClick={notShowAgain} className={styles.buttonLink}>
						Don&apos;t ask me again
					</ButtonLink>
				</Modal.FooterSecondaryActions>
				<Button variant="secondary" onClick={dismissModal}>
					Cancel
				</Button>
				<Button variant="primary" onClick={onSet} disabled={!selection || loading}>
					Set manager
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

interface AddManagerModalWrapperProps {
	viewer: AddManagerModal_viewer$key;
}

export default function AddManagerModalWrapper({ viewer: viewerKey }: AddManagerModalWrapperProps) {
	const modals = useModals();

	const viewer = useFragment<AddManagerModal_viewer$key>(
		graphql`
			fragment AddManagerModal_viewer on Viewer {
				user {
					id
					isGuest
					internalAttributes {
						dateConfigured
						manager {
							id
						}
					}
				}
				account {
					hrisIntegrated
					isPersonalType
					allowInviting
				}
				settings {
					managerDisabled
				}
			}
		`,
		viewerKey,
	);

	const { hasDismissed, dismissedElements } = React.useContext(DismissibleElementsContext);

	const isViable = React.useMemo(() => {
		// Hide the modal in embedded experiences
		const isCandidate = !isZoom && !viewer.user.isGuest && !inVideocallMode && !inCalendarMode;
		if (!isCandidate) return false;

		// Skip if the user is viewing the settings page
		if (window.location.pathname.includes("/settings/")) return false;

		// Skip if the user has indicated they have no manager
		if (viewer.settings.managerDisabled) return false;

		// Skip if the user has already set a manager
		if (viewer.user.internalAttributes?.manager) return false;

		// Account needs to be able to invite users
		if (viewer.account.hrisIntegrated || viewer.account.isPersonalType || !viewer.account.allowInviting) {
			return false;
		}

		const element = dismissedElements.find(element => element.key === DISMISS_ADD_MANAGER_KEY);
		// Ignore if the modal has been temporarily dismissed
		if (element && moment().isBefore(moment(element.lastDismissedAt).add(1, "week"))) {
			return false;
		}

		// Ignore if the modal has been permanently dismissed
		if (hasDismissed(SILENCE_ADD_MANAGER_KEY)) return false;

		// Only target users who have been active for 5+ days
		return moment().diff(viewer.user.internalAttributes?.dateConfigured, "days") >= 5;
	}, [
		viewer.account.allowInviting,
		viewer.account.hrisIntegrated,
		viewer.account.isPersonalType,
		viewer.settings.managerDisabled,
		viewer.user.internalAttributes?.dateConfigured,
		viewer.user.internalAttributes?.manager,
		viewer.user.isGuest,
		hasDismissed,
		dismissedElements,
	]);

	React.useEffect(() => {
		if (isViable) {
			modals.open(
				({ close }) => {
					return <AddManagerModal userId={viewer.user.id} onClose={close} />;
				},
				{ exclusive: true },
			);
		}
	}, [modals, isViable, viewer]);

	return null;
}
